import { HttpClient, HttpParams } from '@angular/common/http';
import {Injectable, Injector} from '@angular/core';
import {map} from 'rxjs/operators';
import {Router} from '@angular/router';

import {environment} from '../../../environments/environment';
import Constants from "../../shared/constants";
import * as Cookies from 'js-cookie';
import {JwtHelperService} from '@auth0/angular-jwt';
import {QueryStringService} from "@xref/ngx";
import {Observable, EMPTY} from "rxjs";


export interface DeprecatedLoginResponseData {
    access: string;
    refresh: string;
    platform: string;
    region: string;
    org: string;
    request_id?: string;
}

export interface LoginResponseData {
    access: string;
    xref_id: string;
    platform: string;
    region: string;
    org: string;
    request_id?: string;
}

export interface DeprecatedLoginResponse {
    status_message: string;
    message: string;
    data: DeprecatedLoginResponseData;
}

export interface LoginResponse {
    status_message: string;
    message: string;
    data: LoginResponseData;
}

export class SsoDTO {
    hd: string;
    code: string;
    email: string;
    id_token: string;
    uuid: string;
    redirect_url: string;
}

@Injectable()
export class IdService {

    url = `${environment.ID_API_OMNI}`;
    returnUrl = '';
    ent_9105 = false;
    private _permissionsService: any = null;

    constructor(
        private http: HttpClient,
        private jwtHelper: JwtHelperService,
        private qs: QueryStringService,
        private router: Router,
        private injector: Injector
    ) {
        this.returnUrl = decodeURIComponent(this.qs.getParam('retUrl'));
        Cookies.set(Constants.RETURN_URL, this.returnUrl, {domain: environment.DOMAIN});
    }

    /**
     * Lazily get PermissionsService to avoid circular dependency.
     * Resolved on first access, then cached for later calls.
     */
    get permissionsService(): any {
        if (!this._permissionsService) {
            const permissionsServiceClass = this.getPermissionsServiceClass();
            this._permissionsService = this.injector.get(permissionsServiceClass);
        }
        return this._permissionsService;
    }

    getPermissionsServiceClass(): any {
        return require('../permissions/permissions.service').PermissionsService;
    }

    getActiveFlags(){
        return this.http.get(`${this.url}feature-flags`);
    }

    createOrganisation(token: string, body: any): Observable<any> {
        const urlWithToken = `${this.url}signup/organisation?t=${token}`;
        return this.http.post(urlWithToken, body).pipe(
            map(res => res)
        );
    }

    rollBackOrgAndUser(token: string, body: any): Observable<any> {
        const urlWithToken = `${this.url}signup/organisation?t=${token}`;
        return this.http.delete(urlWithToken, {
            body: body
        }).pipe(
            map(res => res)
        );
    }

    getJwtAccessToken(data) {
        const urlWithToken = `${this.url}sign-up-authenticate`;
        return this.http.post(urlWithToken, data).pipe(
            map(res => res)
        );
    }

    createUser(token, userData) {
        delete userData.token;
        delete userData.previousUrl;
        const urlWithToken = `${this.url}id?t=${token}`;
        return this.http.post(urlWithToken, userData).pipe(
            map(res => res)
        );
    }

    logUser(token: string, body: any): Observable<any> {
        const urlWithToken = `${this.url}signup/user-details?t=${token}`;

        return this.http.patch(urlWithToken, body).pipe(
            map(res => res)
        );
    }

    getSignupOrganisationDetails(token: string): Observable<any> {
        return this.http.get(`${this.url}signup/organisation?t=${token}`).pipe(
            map(res => res)
        );
    }

    getIdUser(token: string) {
        return this.http
            .get(`${this.url}platform-user?t=${token}`);
    }

    validatePassword(token: string, body: any): Observable<any> {
        const urlWithToken = `${this.url}validate-password?t=${token}`;
        return this.http.post(urlWithToken, body).pipe(
            map(res => res)
        );
    }

    getOriginObservable(uri: string) {
        let params = new HttpParams();
        const orgUid = Cookies.get(Constants.ORG_UID);
        if (orgUid !== undefined) {
            params = params.set('uuid', orgUid);
        }
        return this.http
            .get(`${this.url}origin/${encodeURIComponent(uri)}`, {params});
    }

    requestResetPasswordLink(model: any, successCallback: any = () => { }, errorCallback: any = () => { }) {
        const success = (data: any) => {
            successCallback(data);
        };

        const error = (err: any) => {
            errorCallback(err);
        };

        this.http
            .post(`${this.url}forgot-password/${model.email}`, {})
            .pipe(map(res => res))
            .subscribe(success, error);
    }

    validateResetPasswordToken(token: string, platform?: string, successCallback: any = () => { }, errorCallback: any = () => { }) {
        const success = (data: any) => {
            successCallback(data);
        };

        const error = (err: any) => {
            errorCallback(err);
        };

        const query = platform ? `?platform=${platform}&token=${token}` : `?token=${token}`;
        this.http
            .get(`${this.url}reset-password${query}`)
            .pipe(map(res => res))
            .subscribe(success, error);
    }

    verifySignupToken(token: string, successCallback: any = () => { }, errorCallback: any = () => { }) {
        const success = (data: any) => {
            successCallback(data);
        };

        const error = (err: any) => {
            errorCallback(err);
        };

        this.http
            .post(`${this.url}signup/verify?t=${token}`, {})
            .pipe(map(res => res))
            .subscribe(success, error);
    }

    resetPassword(model: any, successCallback: any = () => { }, errorCallback: any = () => { }) {
        const success = (data: any) => {
            successCallback(data);
        };

        const error = (err: any) => {
            this.responseError(err, errorCallback);
        };

        this.http
            .post(`${this.url}reset-password`, model)
            .pipe(map(res => res))
            .subscribe(success, error);
    }

    requestInvitation(token: any, data: any) {
        return this.http
            .post(`${this.url}request-invitation?t=${token}`, data)
            .pipe(map(res => res));
    }

    getDomainMapping(token: string): Observable<any> {
        return this.http.get(`${this.url}domain-mapping?t=${token}`);
    }

    logButtonClick(token: any, data: any) {
        return this.http
            .post(`${this.url}audit-log?t=${token}`, data)
            .pipe(map(res => res));
    }

    tokenNotExpired(tokenName: string, jwt?: string) {
        const token: string = jwt || Cookies.get(tokenName);
        return token != null && token != undefined && !this.jwtHelper.isTokenExpired(token);
    }

    loggedIn() {
        if (this.ent_9105){
            return Cookies.get(Constants.XREF_ID) != undefined ? true : false;
        }
        return this.tokenNotExpired(Constants.AUTH_TOKEN);
    }

    getAuthToken() {
        if (!this.loggedIn()) {
            this.logout();
        }

        let token = sessionStorage.getItem(Constants.AUTH_TOKEN) || "";

        if (!token || this.jwtHelper.isTokenExpired(token)) {
            const apiRegion = Cookies.get(Constants.API_REGION_TOKEN);
            const selectedOrgUid = Cookies.get(Constants.ORG_UID);
            this.refreshToken(selectedOrgUid, apiRegion);
            token = sessionStorage.getItem(Constants.AUTH_TOKEN) || "";
        }

        if (token == null) {
            this.logout();
            return EMPTY;
        }

        return token;
    }

    refreshToken(org_uid: any, region: any, callback?: any) {
        if (!sessionStorage.getItem(Constants.AUTH_TOKEN) ||
            this.jwtHelper.isTokenExpired(sessionStorage.getItem(Constants.AUTH_TOKEN))) {

            const req = new XMLHttpRequest();
            req.open('POST', `${this.url}refresh`, false);
            req.setRequestHeader('Content-Type', 'application/json; charset=UTF-8');
            req.setRequestHeader('Accept', 'application/json');

            req.onload = function () {
                if (req.status === 200) {
                    // Set new token as cookie
                    const data = JSON.parse(req.response);
                    Cookies.set(Constants.XREF_ID, data['xref_id'], {domain: environment.DOMAIN});
                    sessionStorage.setItem(Constants.AUTH_TOKEN, data['access']);
                    Cookies.set(Constants.API_REGION_TOKEN, data['region'], {domain: environment.DOMAIN});
                    Cookies.set(Constants.ORG_UID, data['organisation'], {domain: environment.DOMAIN});
                    if (callback) callback();
                } else if (req.status === 400) {
                    if (callback) {
                        callback(JSON.parse(req.response));
                    } else {
                        sessionStorage.removeItem(`${Constants.AUTH_TOKEN}`);
                        Cookies.remove(`${Constants.XREF_ID}`, {domain: environment.DOMAIN});
                        Cookies.remove(`${Constants.PLATFORM}`, {domain: environment.DOMAIN});
                        location.assign(`${environment.APP_URL}?retUrl=${encodeURIComponent(location.href)}`);
                    }
                } else {
                    if (callback) callback(new Error());
                }
            };

            req['data'] = {
                'xref_id': Cookies.get(Constants.XREF_ID),
                'uuid': org_uid,
                'region': region,
                'platform': 'xsp'
            };
            req.send(JSON.stringify(req['data']));
        }
    }

    login(successCallback: any = () => {}, errorCallback: any = () => {}, credentials?: any, ssoDto?: SsoDTO) {
        const success = this.ent_9105 ? (res: LoginResponse) => {
            if (res.message === 'User inactive.' || res.message === 'Change Password required.') {
                // Transform response to error response format
                res.status_message = 'client_error';
                res.message = res.data as any;

                return errorCallback(res);
            }

            // Reset
            this.removeToken();
            this.setTokens(res.data);
            this.redirectToReturnUrl();
            successCallback();
        } : (res: DeprecatedLoginResponse) => {
            if (res.message === 'User inactive.' || res.message === 'Change Password required.') {
                // Transform response to error response format
                res.status_message = 'client_error';
                res.message = res.data as any;

                return errorCallback(res);
            }

            // Reset
            this.removeToken();
            this.deprecatedSetTokens(res.data);
            this.DeprecatedRedirectToReturnUrl();
            successCallback();
        };

        const error = (err: any) => {
            this.responseError(err, errorCallback);
        };

        if (credentials) {
            this.http
                .post(`${this.url}authenticate`, credentials, {withCredentials: true})
                .pipe(map(res => res))
                .subscribe(success, error);
        } else {
            this.http
                .post(`${this.url}sso/auth`, ssoDto)
                .pipe(map(res => res))
                .subscribe(success, error);
        }
    }

    responseError(err: any, errorCallback: any) {
        switch (err.status) {
            case 0:
                // Generic network connection issue
                errorCallback({status_message: 'connectivity_error'});
                break;
            case 503:
                // Remove token and set maintenance mode
                this.removeToken();
                errorCallback({status_message: 'maintenance_omni'});
                break;
            default:
                errorCallback(err.error);
                break;
        }
    }

    authenticateSaml(request_id: string, org_id: string, terms: boolean): Observable<any> {
        return this.http
            .post(`${this.url}saml/login`, {
                request_id: request_id,
                org_id: org_id,
                terms: terms,
            })
            .pipe(map((res) => res));
    }

    getSamlMetadata(client_id: string): Observable<any> {
        return this.http
            .get(`${this.url}saml/metadata/${client_id}`)
            .pipe(map((res) => res));
    }

    deprecatedSetTokens(data: DeprecatedLoginResponseData) {
        this.setToken(Constants.AUTH_TOKEN, data.access);
        this.setToken(Constants.REFRESH_TOKEN, data.refresh);
        this.setToken(Constants.PLATFORM, data.platform);
        this.setToken(Constants.API_REGION_TOKEN, data.region);
        this.setToken(Constants.ORG_UID, data.org);
        if (data.request_id) {
            this.setToken(Constants.TRUST_ACCESS, data.request_id);
        }
    }

    setTokens(data: LoginResponseData) {
        this.setSessionToken(Constants.AUTH_TOKEN, data.access);
        this.setToken(Constants.XREF_ID, data.xref_id);
        this.setToken(Constants.PLATFORM, data.platform);
        this.setToken(Constants.API_REGION_TOKEN, data.region);
        this.setToken(Constants.ORG_UID, data.org);
        if (data.request_id) {
            this.setToken(Constants.TRUST_ACCESS, data.request_id);
        }
    }

    /**
     * Persist tokens minted after a successful MFA challenge and redirect the
     * user to their return URL. Shared by the login-time verification flow and
     * the Enterprise (XSP) setup flow, both of which mint login tokens on success.
     */
    completeMfaLogin(data: any) {
        // Enterprise (XSP) MFA during a new-org signup is an identity gate only: once
        // the challenge passes we return to org creation (driven by the registration
        // token) rather than completing login and redirecting to the app. The minted
        // login tokens are intentionally discarded here. The marker is held in
        // sessionStorage so an abandoned signup cannot divert a later normal login.
        if (sessionStorage.getItem(Constants.SIGNUP_MFA_IN_PROGRESS) === 'true') {
            sessionStorage.removeItem(Constants.SIGNUP_MFA_IN_PROGRESS);
            this.router.navigate(['/org-creation']);
            return;
        }

        // Decode before clearing the current session so a malformed response does
        // not leave the user stranded with no auth state.
        const trackingId = this.jwtHelper.decodeToken(data?.access)?.user_id;

        this.removeToken();
        if (this.ent_9105) {
            this.setTokens(data);
            if (trackingId) {
                Cookies.set(Constants.TRACKING_ID, trackingId, {domain: environment.DOMAIN});
            }
            this.redirectToReturnUrl(environment.APP_URL);
        } else {
            this.deprecatedSetTokens(data);
            if (trackingId) {
                Cookies.set(Constants.TRACKING_ID, trackingId, {domain: environment.DOMAIN});
            }
            this.DeprecatedRedirectToReturnUrl(environment.APP_URL);
        }
    }

    setToken(key: string, token: string) {
        Cookies.set(key, token, {domain: environment.DOMAIN});
    }

    getToken(key: string): string {
        return Cookies.get(key);
    }

    setSessionToken(key: string, token: string) {
        sessionStorage.setItem(key, token);
    }

    getSessionToken(key: string): string {
        return sessionStorage.getItem(key);
    }

    removeToken() {
        if (this.ent_9105) {
            sessionStorage.removeItem(Constants.AUTH_TOKEN);
            Cookies.remove(Constants.XREF_ID, {domain: environment.DOMAIN});
            Cookies.remove(Constants.AUTH_TOKEN, {domain: environment.DOMAIN});
            Cookies.remove(Constants.REFRESH_TOKEN, {domain: environment.DOMAIN});
        } else {
            Cookies.remove(Constants.AUTH_TOKEN, {domain: environment.DOMAIN});
            Cookies.remove(Constants.REFRESH_TOKEN, {domain: environment.DOMAIN});
        }
        Cookies.remove(Constants.PLATFORM, {domain: environment.DOMAIN});
        Cookies.remove(Constants.API_REGION_TOKEN, {domain: environment.DOMAIN});
        Cookies.remove(Constants.DELEGATE_AUTH_TOKEN, {domain: environment.DOMAIN});
        Cookies.remove(Constants.TRACKING_ID, {domain: environment.DOMAIN});
        Cookies.remove(Constants.TRUST_ACCESS, {domain: environment.DOMAIN});
    }

    logout(url?: string): void {
        // Remove token
        this.removeToken();

        if (url) {
            this.replaceUrl(`/?retUrl=${encodeURIComponent(url)}`);
        } else {
            this.replaceUrl('/');
        }
    }

    replaceUrl(url: string) {
        location.replace(url);
    }

    DeprecatedRedirectToReturnUrl(fallbackUrl = '') {
        const accessToken = Cookies.get(Constants.AUTH_TOKEN);
        const platform = Cookies.get(Constants.PLATFORM);
        const locale = Cookies.get(Constants.LOCALE_TOKEN);
        if (platform === 'xsp') {
            const omniLocales = Constants.OMNI_LOCALES;
            if (accessToken) {
                let returnUrl;

                if (omniLocales.includes(locale)) {
                    returnUrl = environment.OMNI_URL + locale.toUpperCase() + '/';
                } else {
                    this.setToken(Constants.LOCALE_TOKEN, 'en-au');
                    returnUrl = environment.OMNI_URL + 'EN-AU/';
                }

                const cookiesReturnUrl = Cookies.get(Constants.RETURN_URL);
                if (
                    cookiesReturnUrl != null && cookiesReturnUrl != 'null' &&
                    cookiesReturnUrl.includes('oauth/open-api/')
                ) {
                    returnUrl = Cookies.get(Constants.RETURN_URL);
                    window.location.href = returnUrl;
                }
                if (cookiesReturnUrl != null && cookiesReturnUrl != 'null' &&
                    cookiesReturnUrl.includes('managePermissions') &&
                    cookiesReturnUrl.includes('user')
                ) {
                    returnUrl = Cookies.get(Constants.RETURN_URL);
                    window.location.href = returnUrl;
                    return;
                }

                const trustToken = Cookies.get(Constants.TRUST_ACCESS);
                if (trustToken) {
                    window.location.href = `${environment.TRUST_URL}set-auth-token?ret=${returnUrl}&token=${trustToken}`;
                } else {
                    window.location.href = returnUrl;
                }
            } else {
                location.replace('/');
            }
        } else {
            const employerToken = Cookies.get(Constants.AUTH_TOKEN);

            // If in memory return URL exists use this, otherwise grab stored URL
            let returnUrl = '';

            // Always prioritize session returnUrl, next stored Cookie URL and last provided fallback URL
            if (this.returnUrl && this.returnUrl != 'null') {
                returnUrl = this.returnUrl;
            } else if (Cookies.get(Constants.RETURN_URL) != null && Cookies.get(Constants.RETURN_URL) != 'null') {
                returnUrl = Cookies.get(Constants.RETURN_URL);
            } else if (fallbackUrl != '' && fallbackUrl != 'null' && fallbackUrl != null) {
                returnUrl = fallbackUrl;
            } else {
                returnUrl = environment.APP_URL;
            }

            // Clear Cookie
            Cookies.remove(Constants.RETURN_URL, {domain: environment.DOMAIN});

            let whitelistedDomain = false;

            for (let i = 0; i < environment.RETURN_URL_WHITELIST.length; i++) {
                const contains = returnUrl.indexOf(environment.RETURN_URL_WHITELIST[i]) == 0 || environment.RETURN_URL_WHITELIST[i].indexOf(returnUrl) == 0;

                if (contains) {
                    whitelistedDomain = true;
                    break;
                }
            }

            const allowRedirect = (employerToken && whitelistedDomain);

            if (allowRedirect)
                location.assign(returnUrl);
            else
                location.replace('/');
        }
    }

    redirectToReturnUrl(fallbackUrl = '') {
        const accessToken = Cookies.get(Constants.XREF_ID);
        const platform = Cookies.get(Constants.PLATFORM);
        const locale = Cookies.get(Constants.LOCALE_TOKEN);
        if (platform === 'xsp') {
            const omniLocales = Constants.OMNI_LOCALES;
            if (accessToken) {
                let returnUrl;

                if (omniLocales.includes(locale)) {
                    returnUrl = environment.OMNI_URL + locale.toUpperCase() + '/';
                } else {
                    this.setToken(Constants.LOCALE_TOKEN, 'en-au');
                    returnUrl = environment.OMNI_URL + 'EN-AU/';
                }

                const cookiesReturnUrl = Cookies.get(Constants.RETURN_URL);
                if (
                    cookiesReturnUrl != null && cookiesReturnUrl != 'null' &&
                    cookiesReturnUrl.includes('oauth/open-api/')
                ) {
                    returnUrl = Cookies.get(Constants.RETURN_URL);
                    window.location.href = returnUrl;
                }
                if (cookiesReturnUrl != null && cookiesReturnUrl != 'null' &&
                    cookiesReturnUrl.includes('managePermissions') &&
                    cookiesReturnUrl.includes('user')
                ) {
                    returnUrl = Cookies.get(Constants.RETURN_URL);
                    window.location.href = returnUrl;
                    return;
                }

                const trustToken = Cookies.get(Constants.TRUST_ACCESS);
                if (trustToken) {
                    window.location.href = `${environment.TRUST_URL}set-auth-token?ret=${returnUrl}&token=${trustToken}`;
                } else {
                    window.location.href = returnUrl;
                }
            } else {
                location.replace('/');
            }
        } else {
            const employerToken = Cookies.get(Constants.XREF_ID);

            // If in memory return URL exists use this, otherwise grab stored URL
            let returnUrl = '';

            // Always prioritize session returnUrl, next stored Cookie URL and last provided fallback URL
            if (this.returnUrl && this.returnUrl != 'null') {
                returnUrl = this.returnUrl;
            } else if (Cookies.get(Constants.RETURN_URL) != null && Cookies.get(Constants.RETURN_URL) != 'null') {
                returnUrl = Cookies.get(Constants.RETURN_URL);
            } else if (fallbackUrl != '' && fallbackUrl != 'null' && fallbackUrl != null) {
                returnUrl = fallbackUrl;
            } else {
                returnUrl = environment.APP_URL;
            }

            // Clear Cookie
            Cookies.remove(Constants.RETURN_URL, {domain: environment.DOMAIN});

            let whitelistedDomain = false;

            for (let i = 0; i < environment.RETURN_URL_WHITELIST.length; i++) {
                const contains = returnUrl.indexOf(environment.RETURN_URL_WHITELIST[i]) == 0 || environment.RETURN_URL_WHITELIST[i].indexOf(returnUrl) == 0;

                if (contains) {
                    whitelistedDomain = true;
                    break;
                }
            }

            const allowRedirect = (employerToken && whitelistedDomain);
            sessionStorage.removeItem(Constants.AUTH_TOKEN);
            if (allowRedirect)
                location.assign(returnUrl);
            else
                location.replace('/');
        }
    }

    generateEmailVerificationToken(model: any, successCallback: any = () => { }, errorCallback: any = () => { }) {
        const success = (data: any) => {
            successCallback(data);
        };

        const error = (err: any) => {
            errorCallback(err);
        };

        let payload = {};

        if (this.permissionsService.isFeatureActivated('ent-10781-signup-improvements')) {
            payload = {
                "email": model.email,
                "first_name": model.first_name,
                "last_name": model.last_name,
                "area_code": model.area_code,
                "mobile": model.mobile,
                "org_name": model.org_name,
                "org_size": model.org_size,
                "preferred_language": model.preferred_language,
                "iso_code": model.iso_code
            };
        } else {
            payload = {
                "email": model.email,
                "preferred_language": model.preferred_language
            };
        }

        this.http
            .post(`${this.url}signup/generate-email-verification-token`, payload)
            .pipe(map(res => res))
            .subscribe(success, error);
    }

    signupOriginAuditLog(url: string) {
        let isPricingPage = false, selectedPlanURL = '', plan = '', orgSize = '', currency = '';

        if (url && url.includes('plan') && url.includes('org_size') && url.includes('currency')){
            isPricingPage = true;
            selectedPlanURL = url.replace('/signup?', '').replace('plan=', '').replace('org_size=', '').replace('currency=', '');
            [plan, orgSize, currency] = selectedPlanURL.split('&');
        }

        return this.http
            .post(`${this.url}signup/origin`, {"pricing_page": isPricingPage, "plan": plan, "org_size": orgSize, "currency": currency})
            .pipe(map((res) => res));
    }
}
